type SilhouetteProps = { width: number; height: number; className?: string };

export const WitchSilhouette = ({ width, height, className }: SilhouetteProps) => (
  <svg aria-hidden="true" viewBox="0 0 40 26" width={width} height={height} className={className}>
    {/* Group opacity, so overlapping shapes don't double up. */}
    <g className="fill-white opacity-20">
      <path d="M5 19.5 L37 14.5" fill="none" strokeWidth="1.5" strokeLinecap="round" className="stroke-white" />
      <path d="M0.5 16.5 C3.5 17 5.5 18 8 18.8 L8.3 20.6 C5.5 21 3 22.3 1 24 C2 21.8 2.1 19 0.5 16.5 Z" />
      <path d="M18 11.8 C14 12.2 10.5 13.4 7.3 11.8 C9.8 14.9 13 16.4 16 16.4 Z" />
      <path d="M15 18 C15.4 14.4 17 11.8 19.6 10.9 C21.6 10.3 23.6 11.1 24.4 12.8 L26.8 16.4 C25.2 17 23.7 17.2 22.2 18 L24.4 22 L22.7 22.6 L20.2 18.5 C18.6 18.9 16.6 18.9 15 18 Z" />
      <path d="M23 13.5 L28 15.6 L27.6 16.6 L22.4 14.8 Z" />
      <circle cx="22.4" cy="8.7" r="2.3" />
      <path d="M18.2 7.4 L27.6 6.4 L27.9 7.2 L18.5 8.4 Z" />
      <path d="M19.8 7.4 L18.6 2.2 L16.2 0.6 L19.6 1.2 L24.6 6.9 Z" />
    </g>
  </svg>
);

// The spider art shared by the logo spider (LogoDecor) and the crawler, in its 17×31 viewBox.
// Two sets of four legs that step in opposite phase when walking.
const legsA = [
  "M6.5 21.5 L3.5 19.5 L2 21.5",
  "M6.3 24.6 L3 25.6 L1.8 28",
  "M10.8 23 L14.2 22.4 L15.8 24.6",
  "M10.2 26 L12.6 28 L13.4 30.4",
];
const legsB = [
  "M6.2 23 L2.8 22.4 L1.2 24.6",
  "M6.8 26 L4.4 28 L3.6 30.4",
  "M10.5 21.5 L13.5 19.5 L15 21.5",
  "M10.7 24.6 L14 25.6 L15.2 28",
];
export const spiderLegs = [...legsA, ...legsB];
export const spiderAbdomen = { cx: 8.5, cy: 24.2, rx: 2.6, ry: 3.2 };
export const spiderHead = { cx: 8.5, cy: 20.4, r: 1.7 };

const SpiderLegs = ({ paths, outlined }: { paths: Array<string>; outlined: boolean }) => (
  <>
    {outlined && paths.map((d) => <path key={`halo-${d}`} d={d} strokeWidth="1.8" className="stroke-white/50" />)}
    {paths.map((d) => (
      <path key={d} d={d} />
    ))}
  </>
);

/**
 * Top-down spider (head up) in `currentColor`, with neon eyes: the logo spider's art, without its thread.
 * Its legs step while walking and freeze when a `group` ancestor carries `data-still`.
 */
export const SpiderSilhouette = ({
  width,
  height,
  className,
  outlined = false,
}: SilhouetteProps & {
  /** A light halo, so a dark spider still reads over black. */
  outlined?: boolean;
}) => {
  const step = "group-data-still:animation-paused motion-reduce:animate-none";
  const halo = outlined ? "stroke-white/50" : undefined;
  return (
    <svg aria-hidden="true" viewBox="0 17.5 17 13.5" width={width} height={height} className={className}>
      <g fill="none" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round">
        <g className={`animate-spider-step ${step}`}>
          <SpiderLegs paths={legsA} outlined={outlined} />
        </g>
        <g className={`animate-spider-step-alt ${step}`}>
          <SpiderLegs paths={legsB} outlined={outlined} />
        </g>
      </g>
      <ellipse fill="currentColor" strokeWidth="0.8" className={halo} {...spiderAbdomen} />
      <circle fill="currentColor" strokeWidth="0.8" className={halo} {...spiderHead} />
      <circle className="fill-brand-secondary" cx="7.7" cy="20.1" r="0.75" />
      <circle className="fill-brand-secondary" cx="9.3" cy="20.1" r="0.75" />
    </svg>
  );
};

export const BatSilhouette = ({ width, height, className }: SilhouetteProps) => (
  <svg aria-hidden="true" viewBox="0 0 16 8" width={width} height={height} className={className}>
    <path
      className="fill-white opacity-20"
      d="M8 2.6 C8.6 2.6 9 3 9.2 3.5 C10.5 2 12.5 1 16 1.2 C14.6 2.2 14.4 3.6 14.6 5 C13.4 4.2 12.2 4.4 11.4 5.4 C10.8 4.6 10 4.6 9.4 5.6 L8 7 L6.6 5.6 C6 4.6 5.2 4.6 4.6 5.4 C3.8 4.4 2.6 4.2 1.4 5 C1.6 3.6 1.4 2.2 0 1.2 C3.5 1 5.5 2 6.8 3.5 C7 3 7.4 2.6 8 2.6 Z"
    />
  </svg>
);
